import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

const links = [
  { key: 'services', href: '#services' },
  { key: 'method', href: '#process' },
  { key: 'work', href: '/portfolio' },
  { key: 'blog', href: '/blog' },
  { key: 'about', href: '#about' },
  { key: 'contact', href: '#contact' },
];

export const Nav = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const activeLang = i18n.language || 'en';
  const isPortfolio = location.pathname.includes('/portfolio');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const hashHref = (href) => {
    if (href.startsWith('/')) return `/${activeLang}${href}`;
    return isPortfolio ? `/${activeLang}${href}` : href;
  };

  return (
    <motion.nav
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`sticky top-0 z-50 border-b border-ink transition-colors duration-300 ${
        scrolled ? 'bg-page/95 backdrop-blur-sm' : 'bg-page'
      }`}
      data-testid="nav"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 flex items-center justify-between h-16 md:h-20 gap-4">
        {/* Brand */}
        <Link
          to={`/${activeLang}`}
          className="flex items-center gap-2.5 group shrink-0"
          data-testid="nav-brand"
          aria-label="The Scrappy Marketer"
        >
          <span
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center border border-ink transition-transform duration-500 group-hover:-rotate-3"
            style={{ background: '#D0FF3F' }}
          >
            <img src="/wf-logo.png" alt="" aria-hidden className="h-6 md:h-7 w-auto" />
          </span>
          <span className="font-mono uppercase tracking-tight text-sm md:text-base font-medium text-ink whitespace-nowrap">
            The Scrappy Marketer
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.key}
              href={hashHref(l.href)}
              className="font-mono uppercase tracking-widest text-xs text-ink hover:text-clay transition-colors duration-300 link-underline"
              data-testid={`nav-${l.key}`}
            >
              {t(`nav.${l.key}`)}
            </a>
          ))}
        </div>

        {/* Right side: lang + CTA */}
        <div className="flex items-center gap-3 md:gap-4">
          <LanguageSwitcher />
          <a
            href={hashHref('#contact')}
            className="hidden sm:inline-flex items-center gap-2 bg-ink text-page px-4 md:px-5 py-2.5 font-mono uppercase tracking-widest text-xs border border-ink hover:bg-lime hover:text-ink transition-colors duration-300"
            data-testid="nav-cta-work-with-me"
          >
            {t('nav.cta')}
            <span aria-hidden>↗</span>
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-9 h-9 border border-ink flex flex-col justify-center items-center gap-1"
            aria-label="Menu"
            data-testid="nav-menu-toggle"
          >
            <span className={`block w-4 h-px bg-ink transition-transform ${open ? 'translate-y-[3px] rotate-45' : ''}`} />
            <span className={`block w-4 h-px bg-ink transition-transform ${open ? '-translate-y-[3px] -rotate-45' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="drawer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-ink bg-page"
          >
            <div className="px-5 py-5 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.key}
                  href={hashHref(l.href)}
                  onClick={() => setOpen(false)}
                  className="font-mono uppercase tracking-widest text-sm text-ink"
                  data-testid={`nav-mobile-${l.key}`}
                >
                  {t(`nav.${l.key}`)}
                </a>
              ))}
              <a
                href={hashHref('#contact')}
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-2 bg-ink text-page px-5 py-3 font-mono uppercase tracking-widest text-xs w-fit"
              >
                {t('nav.cta')} <span aria-hidden>↗</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
